import { Injectable, inject, signal } from '@angular/core';
import { PushNotificationService } from './push-notification.service';
import { UserNotification } from '../../models/client/notification.model';

/**
 * Store for the logged-in user's notifications, shown in the bell dropdown.
 * The list and unread count are hydrated from the server feed
 * (PushNotificationService.getMyNotifications) on app load, when the bell
 * opens, and whenever a foreground push arrives — so it always reflects the
 * user's delivered history, including notifications received while the app
 * was closed.
 *
 * unread_count is the server's own count (unopened PushMessageLogs rows
 * across all the user's tokens), so it stays correct across devices.
 * markAllRead() clears it via POST /open-all, the only server-side read
 * mechanism — there's no per-item read endpoint.
 */
@Injectable({ providedIn: 'root' })
export class NotificationStoreService {
  private push = inject(PushNotificationService);

  private _items = signal<UserNotification[]>([]);
  private _unreadCount = signal<number>(0);

  readonly items = this._items.asReadonly();
  readonly unreadCount = this._unreadCount.asReadonly();

  refresh(): void {
    // Don't show any former notifications until the user has enabled notifications (granted permission).
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
      this._items.set([]);
      this._unreadCount.set(0);
      return;
    }
    this.push.getMyNotifications().subscribe({
      next: ({ data, unread_count }) => {
        this._items.set(Array.isArray(data) ? data : []);
        this._unreadCount.set(unread_count ?? 0);
      },
      error: () => {},
    });
  }

  markAllRead(): void {
    this._unreadCount.set(0);
    this.push.openAll().subscribe({
      next: () => {},
      error: () => {},
    });
  }
}
